<template>
  <BgImageLayout>
    <template #title>
      <entity-editor
        :params="license"
        :options="null"
        :value="license.name"
        type="text"
        title="license name"
        @update="saveEdit({ name: $event })"
      >
        <template slot="content">
          <span class="header-title">{{ license.name }}</span>
        </template>
      </entity-editor>
    </template>

    <lm-license-info
      v-if="license"
      :publishers="publishers"
      :license="license"
      :top-bar-actions="topBarActions"
      @refresh="refresh"
    />

    <div v-if="license && license.license_periods.length && quickviewData" class="layout-wrapper">
      <div class="layout">
        <div class="header">
          <div class="left section">
            <enhanced-q-select
              id="form_type"
              v-model="selectedPeriodId"
              :options="licensePeriods"
              class="license-period-dropdown"
              dense
              @input="updateRoute($event, true)"
            />
          </div>
          <div class="right section">
            <q-btn label="Edit" size="sm" class="on-left" color="tertiary" @click.native="showFormModal" />
            <q-btn label="Delete" size="sm" color="negative" @click.native="deleteLicensePeriod(quickviewData)" />
          </div>
        </div>
        <q-separator />
        <div class="content">
          <div class="left section">
            <tab-terms key="tab-terms" :data="quickviewData.terms" />
            <tab-summary key="tab-summary">
              <template #content>
                <div class="text-secondary-1 q-mt-md" v-html="quickviewData.summary"></div>
              </template>
            </tab-summary>
            <TodoList
              :key="selectedPeriodId"
              :component="{ type: 'to-do-list' }"
              :context-url="`/license-period/${selectedPeriodId}`"
              title="Tasks"
              from-page="license-period"
            />
            <lm-notes
              :key="`notes${quickviewData.id}`"
              :entity="{ name: 'license-period', id: quickviewData.id }"
              store-namespace="license-periods"
            />
            <tab-subscriptions key="tab-subscriptions" :data="quickviewData.subscriptions" />
          </div>
          <div class="right section">
            <LicenseFile :file="quickviewData.license_file" deletable @delete="deleteLicenseFile" />

            <lm-attachments
              ref="attachments"
              :key="`attachments${quickviewData.id}`"
              :entity="{ name: 'license-period', id: quickviewData.id }"
              store-namespace="license-periods"
            />

            <lm-links
              :key="`links${quickviewData.id}`"
              :dropdown-initial-value="linksCategories"
              :entity="{ name: 'license-period', id: quickviewData.id }"
              store-namespace="license-periods"
            />
          </div>
        </div>
      </div>

      <form-license-period
        :showing="showModal"
        :license-period="licensePeriodEdit"
        :edit-mode="true"
        @refresh="refresh"
        @close="closeFormModal"
        @publishers-updated="publishers = $event"
      />

      <lm-dialog
        v-for="action in topBarActions"
        :key="action.dialogUid"
        :uid="action.dialogUid"
        :entity="license"
        :callback="redirectToIndex"
        store-namespace="licenses"
      />

      <lm-dialog
        v-if="deletable"
        :uid="uid"
        :entity="deletable"
        :replacement-options="licensePeriodOptions"
        store-namespace="licensePeriods"
        @done="license.license_periods.length > 1 ? deleteAndChangeSelectedPeriod() : redirectToIndex()"
      />
    </div>
  </BgImageLayout>
</template>

<script>
import { linksCategories } from '@/reusable/data/links-categories'
import { LmFormObject } from '@/lib/models/lm-form-object.class'
import { refreshOnIdChange } from '@/mixins/rerender-on-id-change'
// Libraries
import { mapState, mapMutations } from 'vuex'
import { cloneDeep, get } from 'lodash'
import { uid } from 'quasar'
// Components
import TodoModalModule from '@/reusable/TodoModal/vuex/TodoModal'
import LicenseFile from '@/reusable/LicenseFile/LicenseFile'
import FormLicensePeriod from '@components/forms/form-license-period/FormLicensePeriod'
import TodoList from '@/components/general/ToDoList/ToDoList'
import { TabSubscriptions, TabSummary, TabTerms } from '@/reusable/license/index'
import LmAttachments from '@components/lm-blocks/Attachments'
import LmDialog from '@components/lm-blocks/Dialog'
import LmLinks from '@components/lm-blocks/Links'
import LmNotes from '@components/lm-blocks/Notes'
import LmLicenseInfo from './LicenseInfo'
import EnhancedQSelect from '@components/general/EnhancedQSelect'
import EntityEditor from '@components/editors/EntityEditor'

export default {
  name: 'LmPageLicense',
  components: {
    LmAttachments,
    LmLinks,
    LmNotes,
    LmLicenseInfo,
    TabSummary,
    TabTerms,
    TabSubscriptions,
    FormLicensePeriod,
    LmDialog,
    EnhancedQSelect,
    EntityEditor,
    TodoList,
    LicenseFile
  },

  mixins: [refreshOnIdChange()],

  props: {
    id: [Number, String] // from route
  },

  data() {
    return {
      quickviewData: null,
      loadingData: false,
      showModal: false,
      uid: uid(),
      deletable: null,
      selectedPeriodId: null,
      publishers: []
    }
  },

  computed: {
    ...mapState({
      license: state => state.licenses.single,
      route: state => state.route,
      licensePeriodEdit() {
        if (!this.license || !this.quickviewData) {
          return
        }
        return {
          id: this.quickviewData.id,
          license_id: this.license.id,
          publisher_id: this.license.publisher.id,
          name: this.license.name,
          start_date: this.quickviewData.start_date,
          end_date: this.quickviewData.end_date,
          summary: this.quickviewData.summary,
          subscriptions: this.quickviewData.subscriptions,
          owner_id: this.license.owner.id,
          license_file: this.quickviewData.license_file,
          terms: this.quickviewData.terms,
          status: this.quickviewData.status,
          actions: this.quickviewData.actions
        }
      },
      quickviewTitle() {
        return this.quickviewData
          ? `${this.quickviewData.name} (${this.getPeriod(this.quickviewData)})`
          : 'Retrieving data...'
      },
      licensePeriodOptions() {
        return []
      }
    }),
    topBarActions() {
      return {
        delete: {
          name: 'Delete',
          type: 'delete',
          action: 'licenses/deleteConfirmation',
          id: this.license.id,
          dialogUid: uid(),
          textClass: 'text-negative'
        }
      }
    },
    licensePeriods() {
      if (!this.license) return []
      const periods = cloneDeep(this.license.license_periods)
      return periods.map((period, index) => {
        period.label = `(${index + 1}) - ${this.getPeriod(period)}`
        period.value = period.id
        return period
      })
    },
    linksCategories
  },

  watch: {
    selectedPeriodId: {
      handler(periodId) {
        if (periodId) {
          const period = this.license.license_periods.find(p => p.id === periodId)
          period &&
            this.setEntityForTodo({
              name: `${this.license.name} (${this.getPeriod(period)})`,
              id: period.id
            })
        }
      },
      immediate: true
    }
  },

  beforeCreate() {
    this.$store.registerModule('todo-modal', TodoModalModule)
  },

  created() {
    if (this.license && this.license.license_periods.length) {
      const id = this.license.license_periods[0].id
      this.selectedPeriodId = id
      this.updateRoute(id, true)
    }
  },

  beforeDestroy() {
    this.resetCreateData()
    this.$store.unregisterModule('todo-modal')
  },

  methods: {
    ...mapMutations('todo-modal', ['resetCreateData', 'setEntityForTodo']),

    updateRoute(licensePeriodId, withLoader = false) {
      if (licensePeriodId) {
        if (withLoader) this.loadingData = true
        this.$store.dispatch('licensePeriods/getSingle', { id: licensePeriodId }).then(result => {
          this.$set(this, 'quickviewData', result)
          this.loadingData = false
        })
      }
    },
    showFormModal() {
      this.showModal = true
    },
    closeFormModal() {
      this.showModal = false
    },
    refresh(data) {
      this.$refs.attachments.getAttachments()
      if (data) this.quickviewData = data
      this.closeFormModal()
      this.$store.dispatch('licenses/getSingle', { id: this.license.id })
    },
    getPeriod(entry) {
      if (!entry.end_date) return this.formatDateForLocale(entry.start_date)
      return `${this.formatDateForLocale(entry.start_date)} - ${this.formatDateForLocale(entry.end_date)}`
    },
    deleteAndChangeSelectedPeriod() {
      const license = cloneDeep(this.license)
      const periodIndex = license.license_periods.findIndex(l => l.id === this.selectedPeriodId)
      if (~periodIndex) license.license_periods.splice(periodIndex, 1)
      this.$store.commit('licenses/updateSingle', license)
      this.selectedPeriodId = license.license_periods[0].id
      this.updateRoute(license.license_periods[0].id)
    },
    redirectToIndex() {
      this.$router.push({ name: 'licenses' })
    },
    deleteLicensePeriod(params) {
      this.$set(this, 'deletable', params)
      this.$store.dispatch('licensePeriods/deleteConfirmation', {
        id: this.deletable.id,
        dialogUid: this.uid
      })
    },
    deleteLicenseFile() {
      let data = new LmFormObject(cloneDeep(this.quickviewData))
      data.license_file = null
      data.license_id = get(this.quickviewData, 'license.id')
      data.publisher_id = get(this.quickviewData, 'license.publisher.id')
      data.owner_id = get(this.quickviewData, 'owner.id')
      delete data.errors
      delete data.originalData

      let formData = new FormData()
      data.getFormData(formData, data)
      formData.append('_method', 'PUT')
      return this.$store.dispatch(`licensePeriods/update`, { value: formData, id: data.id }).then(this.refresh)
    },
    periodExists(periods) {
      if (!this.selectedPeriodId) return false
      return !!periods.find(period => period.id === this.selectedPeriodId)
    },
    saveEdit(event) {
      return this.$store
        .dispatch('licenses/update', {
          id: this.license.id,
          value: event
        })
        .then(() => this.$store.dispatch('licenses/getSingle', { id: this.license.id }))
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/custom-variables'

#license-file-delete {
  margin-top: -3px;
}

.layout-wrapper {
  margin-top 30px
  width 100%
}
.layout {
  padding 20px
  background #E6E6E6

  .header {
    display grid
    grid-template-columns 1fr auto
    align-items center
    padding 25px 0

    .license-periods-dropdown {
      background $lm-grey
    }

    .section {
      display grid
      grid-template-columns auto auto
      justify-content start
      align-items center
      grid-gap 15px
    }

  }

  .content {
    margin-top 25px
    display grid
    grid-template-columns 2fr 1fr
    grid-gap 20px

    .section {
      display grid
      grid-gap 20px
      &.left {
        grid-template-rows: auto auto 480px auto;
      }
      &.right {
        grid-template-rows: auto 480px 480px auto;
        align-content start
      }
    }
  }
}
</style>
